import { Grid, Hidden } from '@material-ui/core';
import { Form, Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import { FormField } from '../../../../../../global/components/form-element/form-field';
import { FormWrapper } from '../../../../../../global/components/form-wrapper';
import { GuidedProcessSupportBlock } from '../../../../../../global/components/guided-process-support-block';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { FormGroup } from '../../../../../../global/components/layout/form-group';
import { ContentSpacer } from '../../../../../../global/components/layout/generic-content-spacer';
import { MainContent } from '../../../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../../../global/components/layout/sidebar-content';
import { WysiwygWithReactLink } from '../../../../../../global/components/wysiwyg-with-react-link';
import { YesNoOptions } from '../../../../../../global/constants/lists';
import { useApiResponseError } from '../../../../../../global/hooks/useApiError';
import { useGpsType } from '../../../../../../global/hooks/useGpsType';
import DocusignView from '../../../docusign/docusign-view';
import { DocuSignRequest, DocuSignRequestOptions, DocuSignResult } from '../../../docusign/models/docusign-models';
import { LayDcPlanAdoptionContentModel, LayDcPlanFormModel } from '../models/plan-adoption-form-models';
import { QccoPlanAdoptionWizard } from '../wizards/qcco-plan-adoption-wizard';
import { YesNoIndicators } from '../../../../../../global/models/api-model';
import { ButtonWrapper } from '../../../../../../global/components/layout/button-wrapper';

interface Props {
    wizard: QccoPlanAdoptionWizard;
}

function QccoIndemnifyForm(props: Props) {

    const { wizard } = props;
    const content: LayDcPlanAdoptionContentModel = wizard?.getFormContentModel();
    const gpsItem = useGpsType(content?.gpsType);
    const apiErrors = useApiResponseError();
    const currentInstitution = wizard?.wizardOptions?.currentInstitution;
    const initialValues: LayDcPlanFormModel = wizard?.getFormInitialValue();
    const validationSchema = wizard?.getFormValidationSchema();

    const isComplyEpiscopalChurchLayPensionSystemQuestionRequired = wizard?.isComplyEpiscopalChurchLayPensionSystemQuestionRequired();

    const [showDocusignView, setShowDocusignView] = useState(false);

    const docuSignRequest: DocuSignRequest = wizard?.prepareIndemnificationDocuSignRequest();

    const isIndemnificationComplete = wizard?.isIndemnificationComplete();

    const docuSignOptions: DocuSignRequestOptions = {
        request: docuSignRequest,
        finishText: content?.indemnifyDocuSignFinishText,
        rejectText: content?.indemnifyDocuSignRejectText,
        onDecline: () => {
            setShowDocusignView(false);
        },
        onSigningComplete: (result: DocuSignResult) => {

            const completeValues: LayDcPlanFormModel = {
                ...initialValues,
                indemnificationEnvelopeId: result?.envelopeId,
            };

            if (isComplyEpiscopalChurchLayPensionSystemQuestionRequired) {
                completeValues.complyEpiscopalChurchLayPensionSystem = YesNoIndicators.Yes;
            }

            wizard?.onSubmit({
                values: completeValues,
                clientId: currentInstitution?.org_client_nbr,
            });
        },
    };

    function complyChanged(e, formProps: FormikProps<LayDcPlanFormModel>) {
        const { value } = e.target;

        if (value === YesNoIndicators.No) {
            formProps.setFieldValue('indemnificationEnvelopeId', null);
        }
    }

    function renderComplyEpiscopalChurchLayPensionSystemQuestion(formProps: FormikProps<LayDcPlanFormModel>) {

        if (!isComplyEpiscopalChurchLayPensionSystemQuestionRequired) return null;

        return (
            <FormGroup>
                <FormField
                    name='complyEpiscopalChurchLayPensionSystem'
                    label={content?.complyEpiscopalChurchLayPensionSystemQuestion}
                    component="radioGroup"
                    options={YesNoOptions}
                    touched={formProps.touched}
                    errors={formProps.errors}
                    onChange={(e) => complyChanged(e, formProps)}
                    required
                />
            </FormGroup>
        );
    }

    function renderDocusignSignView() {
        if (!showDocusignView) return null;

        return <DocusignView options={docuSignOptions} />;
    }

    function onSign(formProps: FormikProps<LayDcPlanFormModel>) {

        const formValues = formProps?.values;

        if (isIndemnificationComplete && formValues?.indemnificationEnvelopeId) {

            wizard?.onSubmit(
                {
                    values: formValues,
                    clientId: currentInstitution?.org_client_nbr,
                });

            return;
        }

        setShowDocusignView(true);
    }

    return (
        <PageContainer>
            <MainContent>
                <Formik
                    initialValues={initialValues}
                    onSubmit={
                        (values) => wizard?.onSubmit(
                            {
                                values,
                                clientId: currentInstitution?.org_client_nbr,
                            })
                    }
                    enableReinitialize
                    initialErrors={apiErrors}
                    validationSchema={validationSchema}
                >
                    {(formProps: FormikProps<LayDcPlanFormModel>) => (
                        <>
                            <Form>
                                <Grid>
                                    <FormWrapper
                                        formikProps={formProps}
                                        onCancel={() => wizard?.onCancel({
                                            values: formProps.values,
                                            clientId: currentInstitution?.org_client_nbr,
                                        })}
                                        submitDisabled={wizard?.isSubmitDisabled(formProps.values)}
                                        preventButtonDisableValidation={wizard?.preventButtonDisableValidation()}
                                        submitText={content?.submitLabel}
                                        cancelText={content?.cancelLabel}
                                        hideButtons
                                    >
                                        <PageHeader
                                            headerText={content?.pageTitle}
                                            description={content?.PageDescription}
                                        />
                                        <Hidden mdUp>
                                            <SideBarContent>
                                                <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                                            </SideBarContent>
                                        </Hidden>
                                        <ContentSpacer>
                                            {renderComplyEpiscopalChurchLayPensionSystemQuestion(formProps)}
                                        </ContentSpacer>
                                        <DividerSection disabledOverlay={isComplyEpiscopalChurchLayPensionSystemQuestionRequired && (!formProps?.values?.complyEpiscopalChurchLayPensionSystem || formProps?.values?.complyEpiscopalChurchLayPensionSystem === YesNoIndicators.No)} removeDivider >
                                            <ContentSpacer>
                                                <Banner
                                                    banner={{
                                                        type: 'error',
                                                        label: <p><strong>{content?.indemnifyWarningTitle}</strong></p>,
                                                        description: <div>
                                                            <ContentSpacer>
                                                                <WysiwygWithReactLink content={content?.indemnifyWarningInstruction1} />
                                                            </ContentSpacer>
                                                            <ContentSpacer>
                                                                <button className='btn-primary' type='button'
                                                                    onClick={() => onSign(formProps)}>{content?.indemnifyDocuSignSignLabel}</button>
                                                            </ContentSpacer>
                                                            <ContentSpacer>
                                                                <WysiwygWithReactLink content={content?.indemnifyWarningInstruction2} />
                                                            </ContentSpacer>
                                                        </div>,
                                                    }}
                                                />

                                            </ContentSpacer>
                                            {renderDocusignSignView()}
                                        </DividerSection>
                                        <ButtonWrapper flexEnd>
                                            {
                                                isComplyEpiscopalChurchLayPensionSystemQuestionRequired &&
                                                <button className='btn-primary' type='button'
                                                    disabled={!formProps?.values?.complyEpiscopalChurchLayPensionSystem || formProps?.values?.complyEpiscopalChurchLayPensionSystem === YesNoIndicators.Yes}
                                                    onClick={() => wizard?.onSubmit({
                                                        values: formProps.values,
                                                        clientId: currentInstitution?.org_client_nbr,
                                                    })}>{content?.submitLabel}</button>
                                            }
                                            <button className='btn-secondary' type='button'
                                                onClick={() => wizard?.onCancel({
                                                    values: formProps.values,
                                                    clientId: currentInstitution?.org_client_nbr,
                                                })}>{content?.cancelLabel}</button>
                                        </ButtonWrapper>
                                    </FormWrapper>
                                </Grid>
                            </Form>
                        </>
                    )}
                </Formik>
            </MainContent>
            <Hidden smDown>
                <SideBarContent>
                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                </SideBarContent>
            </Hidden>
        </PageContainer>
    );

}

export default QccoIndemnifyForm;